import type { $Fetch } from 'ofetch';
import type { FormularioGateway, ListFormulariosParams } from '@/application/port/out/FormularioGateway';
import type {
  Formulario, FormularioPergunta,
  CreateFormularioCommand, UpdateFormularioCommand, CreateFormularioPerguntaCommand,
} from '@/domain/model/Formulario';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway do CRUD de Formulário (acadadmin slice).
 * Root: `/api/v1/formularios`. Authorities `acadadmin:formulario:read|manage`.
 *
 * @since G.5.14
 */
export class HttpFormularioGateway implements FormularioGateway {
  private readonly base = '/api/v1/formularios';
  constructor(private readonly client: $Fetch) {}

  async list(params: ListFormulariosParams): Promise<Page<Formulario>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.tipo) query.tipo = params.tipo;
    if (params.status) query.status = params.status;
    const res = await this.client<ApiResponse<Page<Formulario>>>(
      this.base, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<Formulario> {
    const res = await this.client<ApiResponse<Formulario>>(
      `${this.base}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async create(cmd: CreateFormularioCommand): Promise<Formulario> {
    const res = await this.client<ApiResponse<Formulario>>(
      this.base, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, cmd: UpdateFormularioCommand): Promise<Formulario> {
    const res = await this.client<ApiResponse<Formulario>>(
      `${this.base}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async publicar(id: string): Promise<Formulario> {
    const res = await this.client<ApiResponse<Formulario>>(
      `${this.base}/${encodeURIComponent(id)}/publicar`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async clonar(id: string): Promise<Formulario> {
    const res = await this.client<ApiResponse<Formulario>>(
      `${this.base}/${encodeURIComponent(id)}/clonar`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async inativar(id: string): Promise<void> {
    // DELETE 204 No Content — não chamar assertApiSuccess (memória [[ava-delete-204]]).
    await this.client(`${this.base}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async listPerguntas(id: string): Promise<FormularioPergunta[]> {
    const res = await this.client<ApiResponse<FormularioPergunta[]>>(
      `${this.base}/${encodeURIComponent(id)}/perguntas`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async addPergunta(id: string, cmd: CreateFormularioPerguntaCommand): Promise<FormularioPergunta> {
    const res = await this.client<ApiResponse<FormularioPergunta>>(
      `${this.base}/${encodeURIComponent(id)}/perguntas`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async removerPergunta(id: string, perguntaId: string): Promise<void> {
    // DELETE 204 No Content.
    await this.client(
      `${this.base}/${encodeURIComponent(id)}/perguntas/${encodeURIComponent(perguntaId)}`,
      { method: 'DELETE' });
  }
}
